import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LanguageService } from 'app/core/i18n/language.service';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';
import { ExportDialogComponent } from 'app/layout/export-overlay/dialog/export-dialog.component';
import { ExportOptions } from 'app/layout/export-overlay/tasks/core/export-task.interfaces';
import { CalendarPeriodicityComponent } from 'app/modules/components/routing/sub-routes/sub-route-periodicity/calendar-periodicity/calendar-periodicity.component';
import { MapModalComponent } from 'app/shared/components/map-modal/map-modal.component';
import {
  MapModalTypeEnum,
  MonitorDocumentTypeEnum,
} from 'app/shared/components/map-modal/models/map-modal.enum';
import { MapModalInputData } from 'app/shared/components/map-modal/models/map-modal.interface';
import { BusinessPartnerBySubRoute } from '../../interfaces/busines-partner-by-sub-route.interface';
import { BpGroupingSelectorComponent } from '../components/bp-grouping-selector/bp-grouping-selector.component';
import { SubRoutePartnersLayoutService } from '../sub-route-partners-layout.service';
import { BPGroupingCriteria } from '../types/bp-grouping.types';
import { unassignedPartnersExportTitle } from './exports/unassigned-partners.constants';
import { UnassignedPartnersExport } from './exports/unassigned-partners.export';
import { PartnerCardComponent } from './partner-card/partner-card.component';
import { UnassignedPartnersGroupedComponent } from './unassigned-partners-grouped/unassigned-partners-grouped.component';
import { UnassignedPartnersPanelService } from './unassigned-partners-panel.service';
import { UnassignedPartnersSidebarService } from './unassigned-partners-sidebar.service';

@Component({
  selector: 'app-unassigned-partners-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    PartnerCardComponent,
    BpGroupingSelectorComponent,
    UnassignedPartnersGroupedComponent,
    TranslatePipe,
  ],
  providers: [
    UnassignedPartnersPanelService,
    UnassignedPartnersSidebarService,
    UnassignedPartnersExport,
  ],
  templateUrl: './unassigned-partners-sidebar.component.html',
  styleUrl: './unassigned-partners-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnassignedPartnersSidebarComponent {
  private dialog = inject(MatDialog);
  private unassignedPartnersExport = inject(UnassignedPartnersExport);
  private panelService = inject(UnassignedPartnersPanelService);
  private layoutService = inject(SubRoutePartnersLayoutService);
  private i18n = inject(LanguageService);
  readonly sidebarService = inject(UnassignedPartnersSidebarService);

  @ViewChild('scrollContainer') scrollContainer!: ElementRef<HTMLElement>;

  // === VIEW MODE & GROUPING FROM PANEL ===
  readonly viewMode = this.panelService.viewMode;
  readonly groupingCriteria = this.panelService.groupingCriteria;
  readonly searchTerm = this.panelService.searchTerm;

  // Expose signals from list service
  readonly data = this.sidebarService.data;
  readonly totalItems = this.sidebarService.totalItems;
  readonly loading = this.sidebarService.loading;
  readonly loadingMore = this.sidebarService.loadingMore;
  readonly hasMore = this.sidebarService.hasMore;

  /**
   * Mongo `_id` of the sub-route's company. A sub-route belongs to exactly one
   * company, so the partner cards open their detail drawer scoped to it and the
   * drawer resolves per-company values (`bpCompanies[]`) instead of the
   * deprecated root copies.
   */
  readonly companyId = computed(
    () => this.layoutService.subRouteData()?.company?._id?.toString() ?? null
  );

  // === SELECTION STATE ===
  readonly selectedIds = signal<Set<string>>(new Set());
  readonly selectedPartners = computed(() =>
    this.data().filter((p) => this.selectedIds().has(p._id))
  );

  // Scroll event handler for infinite scroll
  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const threshold = 100; // pixels from bottom
    const atBottom = element.scrollHeight - element.scrollTop - element.clientHeight < threshold;

    if (atBottom && this.hasMore() && !this.loadingMore()) {
      this.sidebarService.loadMore$.next();
    }
  }

  // Selection methods
  isSelected(id: string): boolean {
    return this.selectedIds().has(id);
  }

  onSelectionChange(id: string, checked: boolean): void {
    this.selectedIds.update((set) => {
      const newSet = new Set(set);
      if (checked) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  }

  clearSelection(): void {
    this.selectedIds.set(new Set());
  }

  // Legacy toggle method (kept for compatibility)
  toggleSelection(id: string): void {
    this.selectedIds.update((set) => {
      const newSet = new Set(set);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }

  // Open calendar modal for a single partner
  openCalendarModal(partner: BusinessPartnerBySubRoute): void {
    const subRouteId = this.sidebarService.subRouteId();
    if (!subRouteId) return;

    const dialogRef = this.dialog.open(CalendarPeriodicityComponent, {
      data: {
        subRouteId: subRouteId,
        businessPartners: [partner],
        frecuencySelected: null,
      },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.sidebarService.reload$.next();
        this.clearSelection();
        this.layoutService.refreshAssignedPartners$.next();
      }
    });
  }

  // Open calendar modal for multiple selected partners
  openBulkCalendarModal(): void {
    const subRouteId = this.sidebarService.subRouteId();
    const partners = this.selectedPartners();
    if (!subRouteId || partners.length === 0) return;

    const dialogRef = this.dialog.open(CalendarPeriodicityComponent, {
      data: {
        subRouteId: subRouteId,
        businessPartners: partners,
        frecuencySelected: null,
      },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.sidebarService.reload$.next();
        this.clearSelection();
        this.layoutService.refreshAssignedPartners$.next();
      }
    });
  }

  // Export
  onExportButtonPressed(): void {
    if (!this.unassignedPartnersExport.canStartTask()) return;

    const dialogRef = this.dialog.open(ExportDialogComponent, {
      data: { title: unassignedPartnersExportTitle() },
    });

    dialogRef.afterClosed().subscribe((options: ExportOptions) => {
      if (options) {
        this.unassignedPartnersExport.export(
          options.format,
          options.range,
          this.totalItems(),
          this.sidebarService.pageSize,
          0 // Always start from page 0 for infinite scroll
        );
      }
    });
  }

  // Grouping change handler
  onGroupingChange(criteria: BPGroupingCriteria): void {
    this.panelService.setGroupingCriteria$.next(criteria);
    this.clearSelection(); // Clear selection when changing view
  }

  // Open map modal to show unassigned partner locations
  openMapModal(): void {
    const subRouteData = this.layoutService.subRouteData();
    if (!subRouteData) return;

    const data: MapModalInputData = {
      type: MapModalTypeEnum.FRECUENCY_ROUTE,
      tripId: null,
      element: { ...subRouteData, type: 'U' },
      document: MonitorDocumentTypeEnum.NONE,
      groupDocuments: [],
      map: {
        displayMode: 'drawer',
        captions: { title: subRouteData.name || this.i18n.t('routing.SUB_ROUTE_PARTNERS.SIDEBAR.MAP_DEFAULT_TITLE') },
      },
    };

    this.dialog.open(MapModalComponent, { width: '90%', data });
  }
}
